/**
 * Painéis das duas saídas da venda assinada (decisão 11), só a partir de
 * "aguardando":
 *
 * - Ligar ao pedido (PCP, `:ligar_venda`): o PCP digitou a venda na planilha
 *   e diz qual é o número. A venda sai da fila e passam a valer as linhas da
 *   planilha. O pedido sugerido (mesmo cliente, vendido perto da assinatura)
 *   vem no topo, mas quem escolhe é o PCP. Pedido com identidade duvidosa
 *   (número em dois clientes, ambiente repetido) fica travado com o motivo,
 *   como no histórico de comentários.
 * - Cancelar (PCP ou quem registrou): distrato ou registro por engano, com o
 *   motivo obrigatório.
 */
import React, { useEffect, useMemo, useState } from "react";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useDados } from "../../context/DadosContext";
import { TAMANHO_MOTIVO_CANCELAMENTO } from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import {
  nomesDosAmbientes,
  pedidosParaLigar,
  rotuloDoPedidoDaPlanilha,
  textoDasDatas,
  textoDosAmbientes,
} from "./apresentacaoVendas";
import { BotaoTexto, Regua, Selo } from "./styles";
import { CorpoPainel, LinhaBotoes, Secao } from "./stylesSimulacao";
import { CampoTexto, ErroInline, ListaPedidos } from "./stylesVendas";

function ResumoDaVenda({ venda }) {
  return (
    <Secao aria-label="Venda">
      <p>
        <strong>{venda.cliente}</strong> · {textoDosAmbientes(venda.ambientes)}
      </p>
      <p>{nomesDosAmbientes(venda.ambientes)}</p>
      <p>{textoDasDatas(venda)}</p>
    </Secao>
  );
}

function useAcaoComErro(acao) {
  const [erro, setErro] = useState(null);
  const executar = async (...args) => {
    setErro(null);
    try {
      return await acao(...args);
    } catch (falha) {
      setErro(falha?.message || "Não foi possível concluir. Tente de novo.");
      throw falha;
    }
  };
  return [erro, executar, setErro];
}

/**
 * `venda` (null = fechado), `onFechar`, `linhas` (linhas classificadas da
 * planilha), `sugeridos` (de `sugestoesDeLigacao`), `onLigar(vendaId, pedido)`.
 */
export function LigarVendaAssinada({ venda, onFechar, linhas, sugeridos, onLigar }) {
  const { dados } = useDados() || {};
  const indice = useMemo(() => (Array.isArray(dados) ? indiceDeIdentidade(dados) : null), [dados]);
  const [busca, setBusca] = useState("");
  const [escolhido, setEscolhido] = useState(null);
  const [erro, ligar, setErro] = useAcaoComErro(async () => {
    await onLigar(venda.id, escolhido.pedido);
    onFechar();
  });

  // Abriu para outra venda: começa pela primeira sugestão livre.
  useEffect(() => {
    if (!venda) return;
    setBusca("");
    setErro(null);
    setEscolhido(null);
  }, [venda, setErro]);

  const { pedidos, total } = useMemo(
    () => (venda ? pedidosParaLigar({ linhas, venda, sugeridos, indice, busca }) : { pedidos: [], total: 0 }),
    [venda, linhas, sugeridos, indice, busca],
  );

  useEffect(() => {
    if (escolhido || busca) return;
    const primeiro = pedidos.find((p) => p.sugerido && !p.bloqueio);
    if (primeiro) setEscolhido(primeiro);
  }, [pedidos, escolhido, busca]);

  const aberto = Boolean(venda);
  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Ligar ao pedido da planilha"
      subtitle="A venda sai da fila e passam a valer as linhas do pedido."
      width="min(94vw, 560px)"
      ariaLabelledBy="ligar-venda-titulo"
      footer={
        aberto ? (
          <LinhaBotoes>
            <AsyncButton
              onClick={ligar}
              disabled={!escolhido || Boolean(escolhido.bloqueio)}
              successMessage={
                escolhido ? `Venda de ${venda.cliente} ligada ao pedido ${escolhido.pedido}.` : undefined
              }
              silentError
            >
              {escolhido ? `Ligar ao pedido ${escolhido.pedido}` : "Escolha o pedido"}
            </AsyncButton>
            <BotaoTexto type="button" onClick={onFechar}>
              Voltar
            </BotaoTexto>
          </LinhaBotoes>
        ) : null
      }
    >
      {aberto ? (
        <CorpoPainel>
          <ResumoDaVenda venda={venda} />
          <Secao aria-label="Pedido da planilha">
            <h3>Qual é o pedido na planilha?</h3>
            <CampoTexto>
              Buscar por número ou cliente
              <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} />
            </CampoTexto>
            {pedidos.length === 0 ? (
              <p>Nenhum pedido da planilha com esse número ou cliente.</p>
            ) : (
              <ListaPedidos role="radiogroup" aria-label="Pedidos da planilha">
                {pedidos.map((p) => (
                  <li key={p.pedidoNormalizado}>
                    <label data-bloqueado={p.bloqueio ? "sim" : "nao"}>
                      <input
                        type="radio"
                        name="pedido-da-planilha"
                        value={p.pedidoNormalizado}
                        checked={escolhido?.pedidoNormalizado === p.pedidoNormalizado}
                        disabled={Boolean(p.bloqueio)}
                        onChange={() => setEscolhido(p)}
                      />
                      <span className="nome" title={rotuloDoPedidoDaPlanilha(p)}>
                        {rotuloDoPedidoDaPlanilha(p)}
                        {p.sugerido ? <Selo>sugestão</Selo> : null}
                      </span>
                      {p.bloqueio ? <span className="bloqueio">{p.bloqueio.mensagem}</span> : null}
                    </label>
                  </li>
                ))}
              </ListaPedidos>
            )}
            {total > pedidos.length ? (
              <Regua>
                Mostrando {pedidos.length} de {plural(total, "pedido", "pedidos")}: busque pelo número para achar os
                outros.
              </Regua>
            ) : null}
            <Regua>
              A venda tem {plural(venda.ambientes.length, "ambiente", "ambientes")}; confira se o pedido escolhido tem
              os mesmos na planilha. Ligar não muda a planilha.
            </Regua>
          </Secao>
          {erro ? <ErroInline role="alert">{erro}</ErroInline> : null}
        </CorpoPainel>
      ) : null}
    </SlideOverPanel>
  );
}

/** `venda` (null = fechado), `onFechar`, `onCancelar(vendaId, motivo)`. */
export function CancelarVendaAssinada({ venda, onFechar, onCancelar }) {
  const [motivo, setMotivo] = useState("");
  const [erro, cancelar, setErro] = useAcaoComErro(async () => {
    await onCancelar(venda.id, motivo.trim());
    onFechar();
  });

  useEffect(() => {
    if (!venda) return;
    setMotivo("");
    setErro(null);
  }, [venda, setErro]);

  const aberto = Boolean(venda);
  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Cancelar venda assinada"
      subtitle="A venda sai da fila e não volta: use para distrato ou registro por engano."
      width="min(94vw, 520px)"
      ariaLabelledBy="cancelar-venda-titulo"
      footer={
        aberto ? (
          <LinhaBotoes>
            <AsyncButton
              variant="danger"
              onClick={cancelar}
              disabled={!motivo.trim()}
              successMessage={`Venda de ${venda.cliente} cancelada.`}
              silentError
            >
              Cancelar a venda
            </AsyncButton>
            <BotaoTexto type="button" onClick={onFechar}>
              Voltar
            </BotaoTexto>
          </LinhaBotoes>
        ) : null
      }
    >
      {aberto ? (
        <CorpoPainel>
          <ResumoDaVenda venda={venda} />
          <Secao aria-label="Motivo">
            <CampoTexto>
              Motivo do cancelamento
              <textarea
                rows={3}
                maxLength={TAMANHO_MOTIVO_CANCELAMENTO}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ex.: cliente desistiu (distrato assinado em 05/10)"
              />
            </CampoTexto>
            <Regua>
              Se o pedido já está na planilha, não cancele: ligue a venda ao pedido, para o histórico mostrar que ela
              virou obra.
            </Regua>
          </Secao>
          {erro ? <ErroInline role="alert">{erro}</ErroInline> : null}
        </CorpoPainel>
      ) : null}
    </SlideOverPanel>
  );
}
